import axios from 'axios';
import { toast } from 'sonner';
import { isStepCompleted, isStepNotNeeded } from './journey-constants';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DP41 commit 1 — EXTRACTION, not a rewrite. Every function below was moved
 * verbatim out of JourneySteps.js so the strip and the step panel can be two
 * components without two copies of the behaviour. Nothing here is new.
 */

// Step mutations — default to the project container; onboarding (or any
// future container) injects its own endpoint via stepUrlBuilder.
export const buildStepUrl = (project, step, stepUrlBuilder) =>
  stepUrlBuilder
    ? stepUrlBuilder(step)
    : `${API}/api/projects/${project.id}/phases/${step.phase.id}/steps/${step.id}`;

// Friendly handling for backend 403 (locked step) — ported from DesignPhasesView.
export const stepErrorToast = (error, fallback) => {
  if (error?.response?.status === 403) {
    toast.error('This step is locked. Unlock it to make changes.');
  } else {
    toast.error(fallback);
  }
};

// ADVISORY dependency hint only — never blocks completion (no backend enforcement).
export const getUnmetDependencies = (step, allSteps) => {
  if (!step?.dependencies?.length) return [];
  const unmet = [];
  for (const dep of step.dependencies) {
    const depStep = (allSteps || []).find(s => s.id === dep || s.name === dep);
    if (depStep && !isStepCompleted(depStep) && !isStepNotNeeded(depStep)) {
      unmet.push(depStep.name);
    }
  }
  return unmet;
};

/** The completion PUT itself, shared by the strip's Enter key and the panel. */
export const completeStepRequest = (url, skipActivityLog = false) => axios.put(url, {
  completed: true,
  status: 'completed',
  completed_at: new Date().toISOString(),
  completed_by: 'Current User',
  skip_activity_log: skipActivityLog,
});

// Smart action routing based on step name + action_type
export const getSmartActions = (step, project, variant = 'project') => {
  const actions = [];
  const stepName = step.name?.toLowerCase() || '';

  // Onboarding steps carry explicit deep links — the project-name
  // heuristics below would misfire (e.g. "Connect email" → Compose
  // Email mailto), so only honor explicit links in that variant.
  if (variant === 'onboarding') {
    if (step.keymade_link) {
      actions.push({ route: step.keymade_link, label: 'Go there now', primary: true, keymade: true });
    }
    if (step.external_link) {
      actions.push({ external: step.external_link, label: step.external_link_label || 'Open External Resource' });
    }
    return actions;
  }

  const actionTypeRoutes = {
    file: { route: '/documents', label: 'Open Documents', icon: 'folder' },
    document: { route: '/documents', label: 'Open Document Library', icon: 'file' },
    email: { route: null, label: 'Compose Email', icon: 'mail', action: 'email' },
    survey: { route: `/projects/${project?.id}/hub?tab=survey`, label: 'Open Client Survey', icon: 'clipboard' },
    invoice: { route: '/financials', label: 'Open Financials', icon: 'dollar' },
    meeting: { route: `/projects/${project?.id}/hub?tab=calendar`, label: 'Schedule Meeting', icon: 'calendar' },
    site_visit: { route: `/projects/${project?.id}/hub?tab=calendar`, label: 'Schedule Site Visit', icon: 'camera' },
    design: { route: `/projects/${project?.id}/hub?tab=mood-boards`, label: 'Open Mood Boards', icon: 'palette' },
    contractor: { route: '/contractors', label: 'Open Contractors', icon: 'hammer' },
    approval: { route: `/projects/${project?.id}/hub?tab=approvals`, label: 'View Approvals', icon: 'check' },
  };

  if (stepName.includes('project in key-made') || stepName.includes('create project')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=info`, label: 'View Project Setup', primary: true });
  }
  if (stepName.includes('dropbox') || stepName.includes('folder')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=files`, label: 'Open Project Files', primary: true });
  }
  if (stepName.includes('xero') || stepName.includes('invoice') || stepName.includes('deposit') || stepName.includes('payment')) {
    actions.push({ route: '/financials', label: 'Open Financials', primary: true });
    if (stepName.includes('xero')) {
      actions.push({ external: 'https://go.xero.com', label: 'Open Xero' });
    }
  }
  if (stepName.includes('email') || stepName.includes('send')) {
    actions.push({ action: 'email', label: 'Compose Email', primary: true });
  }
  if (stepName.includes('survey') || stepName.includes('questionnaire')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=survey`, label: 'Open Survey', primary: true });
  }
  if (stepName.includes('contract') || stepName.includes('agreement') || stepName.includes('proposal')) {
    actions.push({ route: '/documents?type=contracts', label: 'Open Contracts', primary: true });
  }
  if (stepName.includes('mood board') || stepName.includes('design')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=mood-boards`, label: 'Open Mood Boards', primary: true });
  }
  if (stepName.includes('meeting') || stepName.includes('call') || stepName.includes('consultation')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=calendar`, label: 'Schedule Meeting', primary: true });
  }
  if (stepName.includes('takeoff') || stepName.includes('measurement')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=takeoff`, label: 'Open Takeoff Tool', primary: true });
  }
  if (stepName.includes('estimate') || stepName.includes('cost') || stepName.includes('budget')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=estimates`, label: 'Open Cost Estimator', primary: true });
  }
  if (stepName.includes('spec') || stepName.includes('specification')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=specs`, label: 'Open Specifications', primary: true });
  }
  if (stepName.includes('contractor') || stepName.includes('bid')) {
    actions.push({ route: '/contractors', label: 'Manage Contractors', primary: true });
  }
  if (stepName.includes('calendar') || stepName.includes('schedule')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=calendar`, label: 'Open Calendar', primary: true });
  }
  if (stepName.includes('note') || stepName.includes('documentation')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=notes`, label: 'Open Notes', primary: true });
  }
  if (stepName.includes('client portal') || stepName.includes('drawing room')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=portals`, label: 'Open Client Portal', primary: true });
  }
  if (stepName.includes('photo') || stepName.includes('site visit') || stepName.includes('photography')) {
    actions.push({ route: `/projects/${project?.id}/hub?tab=files`, label: 'Upload Photos', primary: true });
  }

  if (actions.length === 0 && step.action_type && actionTypeRoutes[step.action_type]) {
    const config = actionTypeRoutes[step.action_type];
    actions.push({ route: config.route, label: config.label, action: config.action });
  }
  if (step.keymade_link) {
    actions.push({ route: step.keymade_link, label: 'Go to Task Location', keymade: true });
  }
  if (step.external_link) {
    actions.push({ external: step.external_link, label: step.external_link_label || 'Open External Resource' });
  }
  // 2026-02-17 — Compose Email is available on EVERY step. On email-
  // action steps the primary "Compose Email" button already exists
  // above (as `{ action: 'email', primary: true }`); on every other
  // step we add a non-primary Compose Email so the user can still
  // send templated email against this step. The secondary variant
  // is de-duped against any primary email action we already pushed.
  const hasEmailAction = actions.some(a => a.action === 'email');
  if (!hasEmailAction) {
    actions.push({
      action: 'email',
      label: 'Compose Email',
      secondary: true,
    });
  }
  return actions;
};
